import React__default from 'react';
import { Path, Transforms, Editor, Range, Text } from 'slate';
import { ListItemType } from './constants.mjs';
import { isListItem, isList } from './utilities.mjs';
import { findListItems } from './queries/findListItems.mjs';
import { removeList } from './transforms/removeList.mjs';
import { ListItem } from './components/ListItem/ListItem.mjs';
import { getRenderElement } from '../../../../core/rendering/getRenderElement.mjs';
import { findNodes } from '../../../../core/queries/findNode.mjs';
import { isElement } from '../../../../core/utilities/nodes/elements/isElement.mjs';
import { isBlock } from '../../../../core/queries/isBlock.mjs';
import { getDerivedAttributes, getDerivedDataFromHTML } from '../../../../core/utilities/side-effects/combineSideEffects.mjs';
import { serialize } from '../../../../core/utilities/serialize/serialize.mjs';
import { getElementDeserializer } from '../../../../core/serialization/deserialize/utilities/getElementDeserializer.mjs';

const tagName = 'li';
const ListItemPlugin = options => ({
  type: ListItemType,
  renderElement: getRenderElement({
    type: ListItemType,
    rootProps: {},
    component(props) {
      const attributes = getDerivedAttributes({
        element: props.element,
        sideEffects: options === null || options === void 0 ? void 0 : options.sideEffects
      });
      return /*#__PURE__*/React__default.createElement(ListItem, Object.assign({}, props, {
        style: attributes.style
      }));
    }
  }),
  extendEditor(editor) {
    const {
      normalizeNode
    } = editor;
    editor.normalizeNode = entry => {
      const [node, path] = entry;
      const listElements = findNodes(editor, {
        match: isList
      });
      if (isListItem(node)) {
        const isInList = listElements.some(([, listPath]) => Path.isParent(listPath, path));
        if (!isInList) {
          const defaultProps = {
            type: options.defaultBlockType
          };
          // Convert unwrapped list items to default blocks
          Transforms.setNodes(editor, defaultProps, {
            at: path,
            match: isListItem
          });
          return;
        }

        // Unwrap default blocks or untyped elements within list items
        Editor.withoutNormalizing(editor, () => {
          node.children.forEach((child, index) => {
            if (!isElement(child) || child.type === options.defaultBlockType) {
              Transforms.unwrapNodes(editor, {
                at: [...path, index]
              });
            }
          });
        });
      }
      const isChildOfList = listElements.some(([, listPath]) => Path.isChild(path, listPath));

      // Lift block elements within list-items
      // Occurs when deleteForward is triggered on an empty list item
      // In the last position of the list with content after it
      if (isChildOfList && !isListItem(node) && isBlock(editor, node)) {
        Transforms.liftNodes(editor, {
          at: path
        });
        return;
      }
      normalizeNode(entry);
    };
    return editor;
  },
  serialize: {
    element(props) {
      const attributes = getDerivedAttributes({
        sideEffects: options === null || options === void 0 ? void 0 : options.sideEffects,
        element: props.element
      });
      return serialize(tagName, props.children, attributes);
    }
  },
  deserialize: {
    element: getElementDeserializer({
      type: ListItemType,
      rules: [{
        nodeNames: [tagName.toUpperCase()]
      }],
      node: el => {
        const listItemNodeData = {
          type: ListItemType
        };
        return getDerivedDataFromHTML({
          sideEffects: options === null || options === void 0 ? void 0 : options.sideEffects,
          data: listItemNodeData,
          htmlElement: el
        });
      }
    })
  },
  onKeyDown: (event, editor) => {
    const at = editor.selection;
    if (!at || Range.isExpanded(at)) {
      return;
    }

    // Remove empty list items when pressing enter
    if (event.key === 'Enter') {
      const selectedListItem = findListItems(editor, {
        at
      })[0];
      if (selectedListItem) {
        const [itemNode] = selectedListItem;
        if (itemNode.children.length === 1 && Text.isText(itemNode.children[0]) && itemNode.children[0].text === '') {
          event.preventDefault();
          removeList(editor, {
            at,
            defaultBlockType: options.defaultBlockType
          });
        }
      }
    }

    // Delete a list item when it's the first element in the editor and backspace is pressed
    if (event.key === 'Backspace') {
      const selectedListItem = findListItems(editor, {
        at
      })[0];
      if (selectedListItem) {
        const [, itemPath] = selectedListItem;
        const subTree = at.anchor.path.slice(itemPath.length);
        const isCursorAtStartOfListItem = subTree.every(index => index === 0) && at.anchor.offset === 0;
        if (isCursorAtStartOfListItem) {
          event.preventDefault();
          removeList(editor, {
            at,
            defaultBlockType: options.defaultBlockType
          });
        }
      }
    }
  }
});

export { ListItemPlugin };
